/**
 * FeedbackListPage — admin moderation queue. Lives at /feedback.
 *
 * Shows a count strip on top (open / critical / avg rating), a sticky
 * filter bar (status, type, priority, search), and a paginated table
 * of feedback rows. Clicking a row opens the detail page where admins
 * actually act on the feedback.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Search, MessageSquare, AlertCircle, Star } from 'lucide-react';
import { cn } from '../../../infrastructure/lib/utils';
import {
  getFeedbackStats,
  listFeedback,
  type AdminFeedback,
  type AdminFeedbackPriority,
  type AdminFeedbackStatus,
  type AdminFeedbackType,
  type FeedbackStats,
} from '../../../infrastructure/lib/api';
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  PRIORITY_TONE,
  STATUS_TONE,
} from './constants';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function FeedbackListPage() {
  const [items, setItems] = useState<AdminFeedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AdminFeedbackStatus | ''>('');
  const [type, setType] = useState<AdminFeedbackType | ''>('');
  const [priority, setPriority] = useState<AdminFeedbackPriority | ''>('');

  useEffect(() => {
    void getFeedbackStats().then(setStats).catch(() => undefined);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void load(1, false), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, type, priority]);

  async function load(nextPage: number, append: boolean) {
    setLoading(true);
    setError(null);
    try {
      const data = await listFeedback({
        page: nextPage,
        limit: 20,
        search: search || undefined,
        status: status || undefined,
        type: type || undefined,
        priority: priority || undefined,
      });
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setHasMore(nextPage < data.meta.totalPages);
      setPage(nextPage);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-10">
      <header className="space-y-2 max-w-2xl">
        <h2 className="font-display text-4xl font-bold">Feedback Management</h2>
        <p className="text-on-surface-variant">
          Triage user submissions, reply, and drive the resolution workflow.
        </p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<MessageSquare className="w-5 h-5" />}
          label="Total"
          value={stats?.total ?? '—'}
        />
        <StatCard
          icon={<AlertCircle className="w-5 h-5 text-amber-400" />}
          label="Open"
          value={stats?.byStatus.OPEN ?? 0}
        />
        <StatCard
          icon={<AlertCircle className="w-5 h-5 text-rose-400" />}
          label="Critical"
          value={stats?.byPriority.CRITICAL ?? 0}
        />
        <StatCard
          icon={<Star className="w-5 h-5 text-amber-400" />}
          label="Avg rating"
          value={
            stats?.averageRating != null
              ? `${stats.averageRating.toFixed(2)} (${stats.ratingCount})`
              : '—'
          }
        />
      </section>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or description…"
            className="w-full bg-surface-container-lowest border border-border-subtle rounded-xl py-3 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
        </div>
        <Select value={status} onChange={(v) => setStatus(v as AdminFeedbackStatus)} placeholder="All statuses">
          {FEEDBACK_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <Select value={type} onChange={(v) => setType(v as AdminFeedbackType)} placeholder="All types">
          {FEEDBACK_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>
        <Select
          value={priority}
          onChange={(v) => setPriority(v as AdminFeedbackPriority)}
          placeholder="Any priority"
        >
          {FEEDBACK_PRIORITIES.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </Select>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-surface-container-lowest border border-border-subtle rounded-2xl overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wide text-on-surface-variant">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {items.map((fb) => (
                <tr key={fb.id} className="border-t border-border-subtle hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-3 max-w-[240px]">
                    <Link to={`/feedback/${fb.id}`} className="font-semibold hover:text-electric-violet truncate block">
                      {fb.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant truncate max-w-[180px]">
                    {fb.user?.email ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">{fb.type}</td>
                  <td className={cn('px-4 py-3 text-xs', PRIORITY_TONE[fb.priority])}>{fb.priority}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase', STATUS_TONE[fb.status])}>
                      {fb.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">{fb.rating ?? '—'}</td>
                  <td className="px-4 py-3 text-on-surface-variant text-xs">{formatDate(fb.createdAt)}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-on-surface-variant text-sm">
                    No feedback matches these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.section>

      <div className="flex justify-center">
        {hasMore && (
          <button
            onClick={() => void load(page + 1, true)}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl bg-electric-violet/10 border border-electric-violet/30 text-electric-violet text-sm font-semibold hover:bg-electric-violet/20 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="bg-surface-container-lowest border border-border-subtle rounded-2xl p-5">
      <div className="flex items-center gap-2 text-xs text-on-surface-variant uppercase tracking-wide">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
}

function Select({
  value,
  onChange,
  placeholder,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-surface-container-lowest border border-border-subtle rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30 min-w-[160px]"
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}
